import { db } from '@/db/client';
import { goals, goalDeposits, accounts } from '@/db/schema';
import { eq, and, isNull, desc, asc, sql } from 'drizzle-orm';
import { generateId } from '@/lib/id';
import { differenceInMonths, parseISO } from 'date-fns';

// ─── Types ───────────────────────────────────────────────────

export type Goal = typeof goals.$inferSelect;
export type NewGoal = typeof goals.$inferInsert;
export type GoalDeposit = typeof goalDeposits.$inferSelect;

export interface GoalWithAccount extends Goal {
    accountName?: string | null;
    accountColor?: string | null;
    accountIcon?: string | null;
}

export interface GoalSummary {
    totalGoals: number;
    completedGoals: number;
    totalTarget: number;
    totalSaved: number;
    overallProgress: number;
}

export interface GoalProjection {
    avgMonthlyDeposit: number;
    remainingAmount: number;
    estimatedMonths: number | null; // null if no deposits yet
}

// ─── Read ────────────────────────────────────────────────────

/** Quantidade de metas não-excluídas — usado pelo soft-limit do plano grátis. */
export async function countGoals(): Promise<number> {
    const [row] = await db
        .select({ count: sql<number>`COUNT(*)` })
        .from(goals)
        .where(isNull(goals.deletedAt));
    return row?.count ?? 0;
}

export async function getGoals(includeCompleted = false): Promise<GoalWithAccount[]> {
    const conditions = [isNull(goals.deletedAt)];
    if (!includeCompleted) {
        conditions.push(eq(goals.isCompleted, 0));
    }

    const results = await db
        .select({
            id: goals.id,
            name: goals.name,
            targetAmount: goals.targetAmount,
            currentAmount: goals.currentAmount,
            deadline: goals.deadline,
            accountId: goals.accountId,
            icon: goals.icon,
            color: goals.color,
            isCompleted: goals.isCompleted,
            completedAt: goals.completedAt,
            createdAt: goals.createdAt,
            updatedAt: goals.updatedAt,
            deletedAt: goals.deletedAt,
            accountName: accounts.name,
            accountColor: accounts.color,
            accountIcon: accounts.icon,
        })
        .from(goals)
        .leftJoin(accounts, eq(goals.accountId, accounts.id))
        .where(and(...conditions))
        .orderBy(asc(goals.isCompleted), desc(goals.createdAt));

    return results;
}

export async function getGoalById(id: string): Promise<GoalWithAccount | null> {
    const results = await db
        .select({
            id: goals.id,
            name: goals.name,
            targetAmount: goals.targetAmount,
            currentAmount: goals.currentAmount,
            deadline: goals.deadline,
            accountId: goals.accountId,
            icon: goals.icon,
            color: goals.color,
            isCompleted: goals.isCompleted,
            completedAt: goals.completedAt,
            createdAt: goals.createdAt,
            updatedAt: goals.updatedAt,
            deletedAt: goals.deletedAt,
            accountName: accounts.name,
            accountColor: accounts.color,
            accountIcon: accounts.icon,
        })
        .from(goals)
        .leftJoin(accounts, eq(goals.accountId, accounts.id))
        .where(eq(goals.id, id));

    return results[0] ?? null;
}

export async function getGoalDeposits(goalId: string): Promise<GoalDeposit[]> {
    return db
        .select()
        .from(goalDeposits)
        .where(eq(goalDeposits.goalId, goalId))
        .orderBy(desc(goalDeposits.date));
}

export async function getGoalSummary(): Promise<GoalSummary> {
    const all = await db
        .select({
            targetAmount: goals.targetAmount,
            currentAmount: goals.currentAmount,
            isCompleted: goals.isCompleted,
        })
        .from(goals)
        .where(isNull(goals.deletedAt));

    const totalGoals = all.length;
    const completedGoals = all.filter((g) => g.isCompleted === 1).length;
    const totalTarget = all.reduce((s, g) => s + g.targetAmount, 0);
    const totalSaved = all.reduce((s, g) => s + g.currentAmount, 0);
    const overallProgress = totalTarget > 0 ? totalSaved / totalTarget : 0;

    return { totalGoals, completedGoals, totalTarget, totalSaved, overallProgress };
}

export async function getGoalProjection(goalId: string): Promise<GoalProjection> {
    const goal = await getGoalById(goalId);
    if (!goal) return { avgMonthlyDeposit: 0, remainingAmount: 0, estimatedMonths: null };

    const deposits = await getGoalDeposits(goalId);
    const remainingAmount = Math.max(0, goal.targetAmount - goal.currentAmount);

    if (deposits.length === 0) {
        return { avgMonthlyDeposit: 0, remainingAmount, estimatedMonths: null };
    }

    // Calculate average monthly deposit
    const firstDeposit = deposits[deposits.length - 1];
    const lastDeposit = deposits[0];
    const totalDeposited = deposits.reduce((s, d) => s + d.amount, 0);

    const monthsSpan = Math.max(
        1,
        differenceInMonths(parseISO(lastDeposit.date), parseISO(firstDeposit.date)) || 1,
    );

    const avgMonthlyDeposit = totalDeposited / monthsSpan;
    const estimatedMonths = avgMonthlyDeposit > 0
        ? Math.ceil(remainingAmount / avgMonthlyDeposit)
        : null;

    return {
        avgMonthlyDeposit: Math.round(avgMonthlyDeposit * 100) / 100,
        remainingAmount,
        estimatedMonths,
    };
}

// ─── Write ───────────────────────────────────────────────────

export async function createGoal(data: Omit<NewGoal, 'id' | 'createdAt' | 'updatedAt'>): Promise<Goal> {
    const id = generateId();
    const now = new Date().toISOString();
    await db.insert(goals).values({
        ...data,
        id,
        createdAt: now,
        updatedAt: now,
    });
    return (await getGoalById(id))!;
}

export async function updateGoal(id: string, data: Partial<NewGoal>): Promise<void> {
    await db
        .update(goals)
        .set({ ...data, updatedAt: new Date().toISOString() })
        .where(eq(goals.id, id));
}

export async function deleteGoal(id: string): Promise<void> {
    // Meta é alvo virtual (ADR 0015): excluir é só soft-delete. Não há transação
    // nem saldo a estornar — dinheiro de verdade vive na Conta/Reserva, não na meta.
    await db
        .update(goals)
        .set({ deletedAt: new Date().toISOString() })
        .where(eq(goals.id, id));
}

export async function completeGoal(id: string): Promise<void> {
    await db
        .update(goals)
        .set({
            isCompleted: 1,
            completedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        })
        .where(eq(goals.id, id));
}

export async function reopenGoal(id: string): Promise<void> {
    await db
        .update(goals)
        .set({
            isCompleted: 0,
            completedAt: null,
            updatedAt: new Date().toISOString(),
        })
        .where(eq(goals.id, id));
}

export async function addDeposit(
    goalId: string,
    amount: number,
    date: string,
    notes?: string,
): Promise<void> {
    const id = generateId();
    const now = new Date().toISOString();
    const goal = await getGoalById(goalId);

    // Meta é alvo virtual (ADR 0015): depositar só registra o número e marca o
    // progresso — nunca cria transação nem mexe no saldo de conta. Reservar
    // dinheiro de verdade dentro de uma conta é papel da Reserva (Caixinha).
    await db.insert(goalDeposits).values({
        id,
        goalId,
        amount,
        date,
        notes: notes ?? null,
        accountId: null,
        transactionId: null,
        createdAt: now,
    });

    // Update goal's currentAmount
    if (goal) {
        // Arredonda em centavos pra não acumular drift de float a cada depósito.
        const newAmount = Math.round((goal.currentAmount + amount) * 100) / 100;
        await db
            .update(goals)
            .set({
                currentAmount: newAmount,
                updatedAt: new Date().toISOString(),
            })
            .where(eq(goals.id, goalId));

        // Auto-complete if reached target
        if (newAmount >= goal.targetAmount && goal.isCompleted === 0) {
            await completeGoal(goalId);
        }
    }
}

export async function removeDeposit(depositId: string): Promise<void> {
    const deposit = await db
        .select()
        .from(goalDeposits)
        .where(eq(goalDeposits.id, depositId));

    if (deposit.length === 0) return;

    const { goalId, amount } = deposit[0];

    // Depósito de meta é puro acompanhamento: remover só apaga a linha e desfaz o
    // número. Não há transação a estornar nem saldo a recalcular (ADR 0015).
    await db.delete(goalDeposits).where(eq(goalDeposits.id, depositId));

    // Update goal's currentAmount
    const goal = await getGoalById(goalId);
    if (goal) {
        await db
            .update(goals)
            .set({
                currentAmount: Math.max(0, Math.round((goal.currentAmount - amount) * 100) / 100),
                updatedAt: new Date().toISOString(),
            })
            .where(eq(goals.id, goalId));
    }
}
